import { useTypedQueryParams, UseTypedQueryParamsResult } from '../hooks/useTypedQueryParams';
import { z } from 'zod';
import { useEffect, useRef } from 'react';
import { atomWithStorage } from 'jotai/utils';
import { useAtom } from 'jotai';
import { useTypedPathParams } from '../../../shared/fe/hooks/useTypedPathParams';
import { omit } from 'lodash';

const pathParamsSchema = z.object({
	id: z.string(),
});

const filtersQueryParamsSchema = z.object({
	filters: z
		.object({
			duration: z.enum(['-', 'one_time', 'time_limited', 'continuous', 'occurence']).default('-').optional(),
			date: z
				.object({
					start: z.string().datetime({
						offset: true,
					}),
					end: z.string().datetime({
						offset: true,
					}),
				})
				.optional(),
			warningsOnly: z.string().optional(),
			satisfactions: z.array(z.string()).optional(),
			tags: z.array(z.string()).optional(),
			personas: z.array(z.string()).optional(),
			owners: z.array(z.string()).optional(),
		})
		.optional(),
});

type JourneyFilters = NonNullable<z.infer<typeof filtersQueryParamsSchema>['filters']>;

type StorageJourneyFilters = JourneyFilters &
	Readonly<{
		journeyId: string | null;
	}>;

const hasSomeFilter = (filters: JourneyFilters | undefined): boolean => {
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	return filters != null && Object.values(filters).filter((value) => value != null).length > 0;
};

const filtersAtom = atomWithStorage<StorageJourneyFilters>('filters', { journeyId: null });

const useJourneyFilters = (): UseTypedQueryParamsResult<JourneyFilters> => {
	const [storageFilters, setStorageFilters] = useAtom(filtersAtom);
	const [{ filters: paramsFilters }, setParamsFilters] = useTypedQueryParams(filtersQueryParamsSchema);
	const filtersInitialized = useRef(false);
	const { id } = useTypedPathParams(pathParamsSchema);

	useEffect(() => {
		if (filtersInitialized.current) {
			return;
		}

		if (hasSomeFilter(storageFilters) && !hasSomeFilter(paramsFilters) && storageFilters.journeyId !== id) {
			// when user moves to other journey detail, we need to clear previosly stored filters in local storage
			setStorageFilters({ journeyId: null });
			setParamsFilters({});
		} else if (hasSomeFilter(paramsFilters)) {
			// set filters when the user copies URL and someone else visits it
			setStorageFilters({ ...(paramsFilters ?? {}), journeyId: id });
		} else if (hasSomeFilter(storageFilters)) {
			// the user moves to page in which the filters are used, we need to propagate filters to the URL
			setParamsFilters({ filters: storageFilters });
		}
		filtersInitialized.current = true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paramsFilters, storageFilters, id]);

	return [
		omit(storageFilters, ['journeyId']), // we don't want to journeyId computed as a selected filter when showing amount of set filters
		(filters) => {
			// keep both in sync
			setStorageFilters({ ...filters, journeyId: id });
			setParamsFilters({ filters });
		},
	];
};

export { useJourneyFilters, type JourneyFilters };
